import React from "react";
import { Card, Table, Row, Col, Typography } from "antd";
import ApexCharts from "react-apexcharts";

const { Title } = Typography;

// Function to handle specific column name formatting
const formatHeaderTitle = (columnKey) => {
  return columnKey
    // Replace underscore with space
    .replace(/_/g, " ")
    // Insert space before capital letters (for camelCase / PascalCase)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    // Convert to upper case
    .toUpperCase()
    .trim();
};

export default function NewProductSalesTab({ salesNewProduct = [], salesNewProductReport = [] }) {
  const cardStyle = {
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    backgroundColor: "#fff",
    marginBottom: 16,
  };

  const tableProps = {
    size: "small",
    pagination: { pageSize: 10 }, // Set default page size
    bordered: false,
    scroll: { x: 400, y: 500 },
  };

  const summaryColumns = [
    { title: formatHeaderTitle("SalesEmployeeName"), dataIndex: "SalesEmployeeName", key: "SalesEmployeeName" },
    { title: formatHeaderTitle("Qty"), dataIndex: "Qty", key: "Qty", align: "right" },
    { title: formatHeaderTitle("Value"), dataIndex: "Value", key: "Value", align: "right" },
  ];

  const pieLabels = salesNewProduct.map(item => item.SalesEmployeeName);
  const pieSeries = salesNewProduct.map(item => Number(item.Qty) || 0);

  const hasReportData =
    Array.isArray(salesNewProductReport) && salesNewProductReport.length > 0;

  // Build columns safely and detect numeric alignment using any row that has a number in that key
  const dynamicColumns = React.useMemo(() => {
    if (!hasReportData) return [];

    const keys = Object.keys(salesNewProductReport[0] || {});
    return keys.map((key) => {
      const isNumeric = salesNewProductReport.some(
        (row) => typeof row?.[key] === "number" || (!isNaN(Number(row?.[key])) && row?.[key] !== "" && row?.[key] !== null && row?.[key] !== undefined)
      );

      return {
        title: formatHeaderTitle(key),
        dataIndex: key,
        key,
        width: 200,
        align: isNumeric ? "right" : "left",
        render: (value) => {
          if (value === null || value === undefined) return "";
          const num = Number(value);
          return isNumeric && Number.isFinite(num) ? num.toLocaleString() : String(value);
        },
      };
    });
  }, [hasReportData, salesNewProductReport]);


  return (
    <div className="sales-trend-tab" style={{ padding: 16 }}>
      <Row gutter={[16, 16]}>
        {/* Summary Table */}
        <Col xs={24} md={12}>
          <Card
            title={<span style={{ fontSize: 16, fontWeight: 600, color: "#1976d2" }}>New Product Sales Summary</span>}
            style={cardStyle}
            bodyStyle={{ padding: 12 }}
          >
            <Table
              columns={summaryColumns}
              dataSource={salesNewProduct}
              rowKey="SalesEmployeeName"
              {...tableProps}
              summary={pageData => {
                let totalQty = 0;
                let totalValue = 0;
                pageData.forEach(({ Qty, Value }) => {
                  totalQty += Number(Qty) || 0;
                  totalValue += Number(Value) || 0;
                });
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell><b>Grand Total</b></Table.Summary.Cell>
                    <Table.Summary.Cell align="right"><b>{totalQty.toLocaleString()}</b></Table.Summary.Cell>
                    <Table.Summary.Cell align="right"><b>{totalValue.toLocaleString()}</b></Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </Col>

        {/* Pie Chart */}
        <Col xs={24} md={12}>
          <Card
            title={<span style={{ fontSize: 16, fontWeight: 600, color: "#1976d2" }}>Sales Distribution (Qty)</span>}
            style={cardStyle}
            bodyStyle={{ padding: 12, minHeight: 360 }}
          >
            {pieSeries.length > 0 ? (
              <ApexCharts
                type="donut"
                series={pieSeries}
                height={420}
                options={{
                  chart: {
                    type: "donut",
                    animations: {
                      enabled: true,
                      easing: "easeinout",
                      speed: 1800,
                    },
                  },
                  labels: pieLabels,
                  colors: ["#1976d2", "#d32f2f", "#388e3c", "#fbc02d", "#7b1fa2", "#0097a7", "#f57c00"],
                  fill: {
                    type: "gradient",
                  },
                  legend: {
                    position: "bottom",
                    fontSize: "11px",
                    labels: {
                      colors: "#333",
                    },
                    markers: {
                      radius: 12,
                    },
                  },
                  dataLabels: {
                    enabled: true,
                    dropShadow: {
                      enabled: false,
                    },
                    formatter: function (val, opts) {
                      return `${val.toFixed(1)}%`;
                    },
                    style: {
                      fontSize: "13px",
                      fontWeight: 500,
                    },
                  },
                  tooltip: {
                    y: {
                      formatter: (val) => `${val.toLocaleString()} units`,
                    },
                  },
                  plotOptions: {
                    pie: {
                      donut: {
                        size: "60%",
                        labels: {
                          show: true,
                          name: {
                            show: true,
                            fontSize: "16px",
                            offsetY: -10,
                          },
                          value: {
                            show: true,
                            fontSize: "20px",
                            fontWeight: 700,
                            color: "#333",
                            offsetY: 10,
                            formatter: (val) => `${val.toLocaleString()}`,
                          },
                          total: {
                            show: true,
                            label: "Total Qty",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#666",
                            formatter: function (w) {
                              return w.globals.seriesTotals.reduce((a, b) => a + b, 0).toLocaleString();
                            },
                          },
                        },
                      },
                    },
                  },
                  responsive: [
                    {
                      breakpoint: 768,
                      options: {
                        chart: { height: 280 },
                        legend: { position: "bottom" },
                      },
                    },
                  ],
                }}
              />
            ) : (
              <div style={{ textAlign: "center", paddingTop: 80 }}>No data available</div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Detailed Table */}
      <Row style={{ marginTop: 20 }}>
        <Col span={24}>
          <Card
            title="New Product Sales Detail Report"
            style={{
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              backgroundColor: "#fff",
              marginBottom: 16,
            }}
            bodyStyle={{ padding: 12 }}
          >
            <Table
              dataSource={salesNewProductReport}
              columns={dynamicColumns}
              rowKey={(record, idx) => idx}
              size="small"
              scroll={{ x: "max-content", y: 500 }}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
